import { addDays, format } from 'date-fns';
import { StorePerformanceInterval } from '../../constants';
import { Store, StoreDetails } from '../../global-state/types';
import {
  ConversionRatePerformanceItem,
  NewMembersPerformanceItem,
  ProfitLossResponse,
  SalesNetPerformanceItem,
  ShareOfReceiptsItem,
  StorePerformanceItem,
} from '../../hooks';
import { getTodayPerformanceItems } from '../../utils/storePerformanceChart';
import {
  getConversionRateData,
  getMockCustomerEngagementForecastData,
  getMockSCOShareOfReceiptsData,
  getMockShareOfReceiptsData,
  getNewMembersData,
  getQueueLengthForecastData,
  getSalesNetWidgetData,
} from './transaction-search.mock';

export const STARTDATE = format(new Date(), 'yyyy-MM-dd');
export const ENDDATE = format(addDays(new Date(), 2), 'yyyy-MM-dd');

const timing1 = {
  open: {
    opens: '10:00',
    closes: '19:00',
  },
  exception: {
    opens: '',
    closes: '',
  },
};

const timing2 = {
  open: {
    opens: '10:00',
    closes: '17:00',
  },
  exception: {
    opens: '',
    closes: '',
  },
};

const openingHours = {
  mon: timing1,
  tue: timing2,
  wed: timing1,
  thu: timing1,
  fri: timing2,
  sat: timing2,
  sun: timing1,
};

const openingHourExceptions = {
  item: [
    { date: '2022-11-22', openingHours: { opens: '10:00', closes: '21:00' }, isClosedAllDay: false },
    { date: '2022-11-24', openingHours: { opens: '10:00', closes: '22:00' }, isClosedAllDay: false },
    { date: '2022-11-26', openingHours: { opens: '10:00', closes: '18:00' }, isClosedAllDay: true },
  ],
};

export const mockAllStoresDetails = {
  brands: ['H&M', 'COS', 'Monki', 'Weekday'],
  countryCodes: ['SE', 'DK', 'NO'],
  jobRoles: [
    {
      jobRoleCode: '45741560',
      scope: 'Area',
      jobDescription: 'areaManager',
    },
    {
      jobRoleCode: '38341548',
      scope: 'Area',
      jobDescription: 'areaVisual',
    },
    {
      jobRoleCode: '37163617',
      scope: 'Country',
      jobDescription: 'countryController',
    },
    {
      jobRoleCode: '3000396',
      scope: 'Region',
      jobDescription: 'regionalOther',
    },
    {
      jobRoleCode: '3000431',
      scope: 'Global (All Stores within Brand)',
      jobDescription: 'globalRole',
    },
    {
      jobRoleCode: '3000438',
      scope: 'Region',
      jobDescription: 'regionalManager',
    },
    {
      jobRoleCode: '3000233',
      scope: 'Sales Market',
      jobDescription: 'salesMarketOperation',
    },
    {
      jobRoleCode: '3000413',
      scope: 'Region',
      jobDescription: 'regionalVisual',
    },
    {
      jobRoleCode: '3000417',
      scope: 'Sales Market',
      jobDescription: 'salesMarketVisual',
    },
    {
      jobRoleCode: '3000294',
      scope: 'Sales Market',
      jobDescription: 'salesMarketHR',
    },
  ],
  organisations: [
    { organisationId: '5230010', organisationName: 'SE Stockholm', brandId: 0 },
    { organisationId: '52301210', organisationName: 'SE Maradalen', brandId: 3 },
  ],
  businessUnits: [
    { businessUnitId: '50010', businessUnitName: 'North Europe', brandId: 0 },
    { businessUnitId: '51210', businessUnitName: 'Americas', brandId: 3 },
  ],
};

export const mockStoresDetails: StoreDetails[] = [
  {
    storeId: 'SE0038',
    name: 'H&M - Drottninggatan 56 - Stockholm',
    area: 'SE Mälardalen',
    longitude: '11.96734995286636',
    latitude: '57.70456331254925',
    address: 'Drottninggatan 56, Stockholm, 111 21',
    timezone: 'Europe/Stockholm',
    status: 'OPEN',
    currency: 'SEK',
    corporateBrandId: 0,
    businessUnitId: '23324',
    businessUnitName: 'America',
    closingDate: '0000-00-00',
  },
  {
    storeId: 'SE0663',
    name: 'H&M - Farsta Centrum - Farsta',
    area: 'SE Mälardalen',
    latitude: '57.70456331254925',
    longitude: '11.96734995286636',
    address: 'Farsta Torg 10, Farsta, 123 47',
    timezone: 'Europe/Berlin',
    status: 'OPEN',
    currency: 'SEK',
    corporateBrandId: 3,
    businessUnitId: '23324',
    businessUnitName: 'America',
    closingDate: '0000-00-00',
  },
  {
    storeId: 'SE0144',
    name: 'H&M - Mall Of Scandinavia - Solna',
    area: 'Box Area Demo',
    longitude: '18.003338',
    latitude: '59.370186',
    address: 'Evenemangsgatan 14C, Solna, 16979',
    timezone: 'Europe/Stockholm',
    status: 'CLOSED',
    currency: 'SEK',
    corporateBrandId: 1,
    businessUnitId: '2314',
    businessUnitName: 'Canada',
    closingDate: '0000-00-00',
  },
];

export const mockStores: Store[] = [
  {
    id: '1ec4df22-9fa4-4b69-b1f8-d51c781e922c',
    storeId: 'SE0038',
    area: 'SE Mälardalen',
    popularName: 'H&M - Drottninggatan 56 - Stockholm',
    countryCode: 'SE',
    status: 'OPEN',
    currency: 'SEK',
    openingHours: { ...openingHours },
    openingHourExceptions: { ...openingHourExceptions },
    storeManager: {
      firstName: 'John',
      lastName: 'Doe',
      upn: 'john.doe@example.com',
    },
    addresses: {
      item: [
        {
          addressType: 'Location',
          streetAddress1: 'Drottninggatan 56',
          postalCity: 'Stockholm',
          zipCode: '111 21',
        },
      ],
    },
    kpis: [
      { name: 'dailyConversionRate', target: 20, locked: true },
      { name: 'dailyTurnOver', target: 10000 },
      { name: 'dailyNewMembers', target: 5, locked: true },
      { name: 'dailyShareOfReceipts', target: 10 },
    ],
    corporateBrandId: 0,
    exclusionListCount: [
      {
        startDate: STARTDATE,
        endDate: ENDDATE,
      },
    ],
  },
  {
    id: '24f9315e-cd43-4bfc-af4c-4f9e82d5b16c',
    storeId: 'SE0663',
    area: 'SE Mälardalen',
    popularName: 'Kungsgatan 51',
    countryCode: 'SE',
    status: 'OPEN',
    currency: 'SEK',
    openingHours: { ...openingHours },
    openingHourExceptions: { ...openingHourExceptions },
    storeManager: {
      firstName: 'Jane',
      lastName: 'Smith',
      upn: 'jane.smith@example.com',
    },
    addresses: {
      item: [
        {
          addressType: 'Location',
          streetAddress1: 'Kungsgatan 51',
          postalCity: 'Stockholm',
          zipCode: '111 22',
        },
      ],
    },
    kpis: [
      { name: 'dailyConversionRate', target: 20, locked: true },
      { name: 'dailyTurnOver', target: 10000 },
      { name: 'dailyNewMembers', target: 5, locked: true },
      { name: 'dailyShareOfReceipts', target: 10 },
    ],
    corporateBrandId: 3,
    exclusionListCount: [
      {
        startDate: STARTDATE,
        endDate: ENDDATE,
      },
    ],
  },
  {
    id: '24f9315e-cd43-4bfc-af4c-4f9e82d5b16c',
    storeId: 'SE0144',
    area: 'SE Mälardalen 1',
    popularName: 'Weekday - Kungsgatan 51',
    countryCode: 'SE',
    currency: 'SEK',
    status: 'CLOSED',
    openingHours: { ...openingHours },
    openingHourExceptions: { ...openingHourExceptions },
    storeManager: {
      firstName: 'Alice',
      lastName: 'Johnson',
      upn: 'alice.johnson@example.com',
    },
    addresses: {
      item: [
        {
          addressType: 'Location',
          streetAddress1: 'Kungsgatan 51',
          postalCity: 'Stockholm',
          zipCode: '111 22',
        },
      ],
    },
    kpis: [
      { name: 'dailyConversionRate', target: 25 },
      { name: 'dailyTurnOver', target: 10000 },
      { name: 'dailyNewMembers', target: 8 },
      { name: 'dailyShareOfReceipts', target: 10 },
    ],
    corporateBrandId: 0,
    exclusionListCount: [
      {
        startDate: STARTDATE,
        endDate: ENDDATE,
      },
    ],
  },
];

export const getMockHighlightedWidgets = () => {
  return ['dailyShareOfReceipts', 'dailyTurnOver', 'dailyNewMembers'];
};

export const getMockWeatherData = () => {
  const data = {
    name: 'Eskilstuna',
    situation: 'overcast clouds',
    icon: 'cloudy',
    units: 'Celsius',
    temperature: 2,
    temperatureHigh: 2,
    temperatureLow: 3,
  };
  return data;
};

export const forecastData = (kpi: string): StorePerformanceItem[] => {
  let storePerformanceItems: StorePerformanceItem[] = [];
  let storePerformanceCompleteItems: StorePerformanceItem[] = [];

  const kpiGetHasValue = (currentKpi: string, item: StorePerformanceItem) => {
    if (currentKpi === 'newMembers') return (item as NewMembersPerformanceItem).totalTransactions !== 0;

    if (currentKpi === 'shareOfReceipt') return (item as ShareOfReceiptsItem).totalTransactions !== 0;

    if (currentKpi === 'netSale') return (item as SalesNetPerformanceItem).netSales !== 0;

    if (currentKpi === 'conversionRate')
      return (
        (item as ConversionRatePerformanceItem).payingCustomers !== 0 ||
        (item as ConversionRatePerformanceItem).nonPayingCustomers !== 0
      );
    return false;
  };

  if (kpi === 'newMembers') {
    storePerformanceItems = getNewMembersData().newMembers;
    storePerformanceCompleteItems = getNewMembersData(false).newMembers;
  }

  if (kpi === 'shareOfReceipt') {
    storePerformanceItems = getMockShareOfReceiptsData().shareOfReceipts;
    storePerformanceCompleteItems = getMockShareOfReceiptsData(false).shareOfReceipts;
  }

  if (kpi === 'netSale') {
    storePerformanceItems = getSalesNetWidgetData().netSales;
    storePerformanceCompleteItems = getSalesNetWidgetData(false).netSales;
  }

  if (kpi === 'conversionRate') {
    storePerformanceItems = getConversionRateData().conversionRate;
    storePerformanceCompleteItems = getConversionRateData(false).conversionRate;
  }

  if (kpi === 'shareOfReceiptSCO') {
    storePerformanceItems = getMockSCOShareOfReceiptsData().shareOfReceiptsSCO;
    storePerformanceCompleteItems = getMockSCOShareOfReceiptsData(false).shareOfReceiptsSCO;
  }

  if (kpi === 'netSaleSCO') {
    storePerformanceItems = getSalesNetWidgetData().netSales;
    storePerformanceCompleteItems = getSalesNetWidgetData(false).netSales;
  }

  if (kpi === 'queueLength') {
    return getQueueLengthForecastData();
  }

  if (kpi === 'customerEngagement') {
    return getMockCustomerEngagementForecastData();
  }

  const filteredPerformanceData = getTodayPerformanceItems({
    interval: StorePerformanceInterval.DAILY,
    currentUTCTime: new Date(),
    storeTimezone: mockStoresDetails[0].timezone,
    storePerformanceItems: storePerformanceItems,
    getHasValue: (item) => kpiGetHasValue(kpi, item),
  });

  const forecastItems: StorePerformanceItem[] = [];

  storePerformanceCompleteItems.forEach((item, index) => {
    if (filteredPerformanceData[index]) return;

    forecastItems.push(item);
  });

  return forecastItems;
};

export const getMockProfitLossData = (): ProfitLossResponse => {
  const data = {
    lastUpdated: new Date(),
    salesExcVat: {
      actual: 2107905.51,
      budget: 2323678.8726,
      lastYear: 0,
    },
    salesIncVat: {
      actual: 2634887.31,
      budget: 2903902.04,
      lastYear: 0,
    },
    details: {
      otherCosts: [
        {
          name: 'Other selling expenses',
          actual: 16208.22,
          budget: 261.37,
          lastYear: 0,
        },
        {
          name: 'Cash differences',
          actual: 12,
          budget: 262.916,
          lastYear: 0,
        },
        {
          name: 'Office supplies and printed m',
          actual: 1735.75,
          budget: 5532.6771,
          lastYear: 0,
        },
        {
          name: 'IT',
          actual: 40730.78,
          budget: 13329.3041,
          lastYear: 0,
        },
        {
          name: 'Credit cards and Bank Costs',
          actual: 10993.05,
          budget: 10851.4829,
          lastYear: 0,
        },
        {
          name: 'Other external services, Fees',
          actual: 1871.92,
          budget: 1027.7838,
          lastYear: 0,
        },
        {
          name: 'Shopfitting and Bulbs',
          actual: 28.95,
          budget: 3115.9585,
          lastYear: 0,
        },
        {
          name: 'Security and other risk-relat',
          actual: 1706.25,
          budget: 5942.918,
          lastYear: 0,
        },
        {
          name: 'Maintenance on premises',
          actual: 2697.62,
          budget: 4764.59,
          lastYear: 0,
        },
        {
          name: 'Electricity',
          actual: 9025.99,
          budget: 18260,
          lastYear: 0,
        },
        {
          name: 'Consumable equipment and supp',
          actual: 2150.18,
          budget: 4730.2556,
          lastYear: 0,
        },
        {
          name: 'Hangers',
          actual: 1654.29,
          budget: 1705.1229,
          lastYear: 0,
        },
        {
          name: 'Cleaning, Pest and Waste',
          actual: 18378,
          budget: 28510.35,
          lastYear: 0,
        },
        {
          name: 'Travel expenses',
          actual: 161.7,
          budget: 0,
          lastYear: 0,
        },
        {
          name: 'Freight and transport',
          actual: 0,
          budget: 531.32,
          lastYear: 0,
        },
      ],
      reductions: [
        {
          name: 'Reduction cosmetics',
          actual: 5682.57,
          budget: 5486.6563,
          lastYear: 0,
        },
        {
          name: 'Reduction textile',
          actual: 102179.28,
          budget: 95528.8734,
          lastYear: 0,
        },
      ],
      salaries: [
        {
          name: 'Fixed salaries',
          actual: 428091.89,
          budget: 373618.2375,
          lastYear: 0,
        },
        {
          name: 'Flexible salaries',
          actual: 71847.46,
          budget: 66097.5371,
          lastYear: 0,
        },
        {
          name: 'Vacation',
          actual: 48434.14,
          budget: 45732.385,
          lastYear: 0,
        },
        {
          name: 'Illness',
          actual: 12095.84,
          budget: 1516.7174,
          lastYear: 0,
        },
        {
          name: 'Overtime',
          actual: 235.8,
          budget: 99.199,
          lastYear: 0,
        },
        {
          name: 'Other personnel costsEmploye',
          actual: 3583.15,
          budget: 5829.924,
          lastYear: 0,
        },
      ],
      scrapping: [
        {
          name: 'scrapping',
          actual: 5016.42,
          budget: 13148.9884,
          lastYear: 0,
        },
      ],
      properties: [
        {
          name: 'Property maintenance',
          actual: 12540.12,
          budget: 15000,
          lastYear: 0,
        },
      ],
      surplus1: [
        {
          name: 'surplus1',
          actual: 144132.55,
          budget: 406477.7656,
          lastYear: 0,
        },
      ],
      shrinkage: [
        {
          name: 'shrinkage',
          actual: 41858.6,
          budget: 43157,
          lastYear: 0,
        },
      ],
    },
    monthlyBreakdown: [
      {
        month: '202601',
        salesExcVat: 370000,
        expenses: 210000,
        surplus1: 160000,
      },
      {
        month: '202602',
        salesExcVat: 355000,
        expenses: 225000,
        surplus1: 130000,
      },
      {
        month: '202603',
        salesExcVat: 342000,
        expenses: 212000,
        surplus1: 130000,
      },
      {
        month: '202604',
        salesExcVat: 330000,
        expenses: 410000,
        surplus1: -80000,
      },
      {
        month: '202605',
        salesExcVat: 318000,
        expenses: 378000,
        surplus1: -60000,
      },
    ],
  };

  return data;
};
